/**
 * 🟥 CUADRO NEGRO 2026-07-02 — Modal Auto-Visual Creation (Fase 2 FULL).
 *
 * Escucha el evento window `damyxia:auto-visual-open` con
 * `{verb, userText}` y muestra 2 opciones:
 *   • Skeleton → precarga el skeleton oficial del verbo.
 *   • Generar con IA → llama a /api/lyon-assist/assist (modo rewrite) con el
 *     texto del usuario + skeleton, y usa el código generado.
 *
 * En ambos casos guarda el payload en sessionStorage.__damyxia_avc__ y
 * dispara `damyxia:open-internal-workspace` para que App.js abra el Workspace.
 * `InternalWorkspace.jsx` consume el payload al montar y crea el módulo
 * con el código ya cargado.
 */
import { useEffect, useState } from 'react';
import { X, Sparkles, FileCode, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api';
const SS_KEY = '__damyxia_avc__';

export default function AutoVisualCreationModal() {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState(null);   // {verb, userText}
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    const onOpen = (ev) => {
      const detail = ev.detail || {};
      if (!detail.verb) return;
      setPayload({ verb: detail.verb, userText: detail.userText || '' });
      setOpen(true);
    };
    const onEsc = (ev) => { if (ev.key === 'Escape') setOpen(false); };
    window.addEventListener('damyxia:auto-visual-open', onOpen);
    window.addEventListener('keydown', onEsc);
    return () => {
      window.removeEventListener('damyxia:auto-visual-open', onOpen);
      window.removeEventListener('keydown', onEsc);
    };
  }, []);

  if (!open || !payload) return null;

  const { verb, userText } = payload;

  const stashAndOpen = (code, source /* 'skeleton' | 'ai' */) => {
    try {
      sessionStorage.setItem(SS_KEY, JSON.stringify({
        verb_id: verb.id,
        verb_label: verb.verb,
        code,
        source,
        user_text: userText,
        created_at: new Date().toISOString(),
      }));
    } catch (e) {
      console.warn('[AVC] sessionStorage failed:', e);
    }
    window.dispatchEvent(new CustomEvent('damyxia:open-internal-workspace', {
      detail: { source: 'auto-visual-creation', verb_id: verb.id },
    }));
    setOpen(false);
    toast.success(
      source === 'skeleton'
        ? `Skeleton "${verb.verb}" cargado en el Workspace`
        : `Código IA generado y cargado en el Workspace`,
      { duration: 3500 }
    );
  };

  const handleSkeleton = () => {
    stashAndOpen(verb.skeleton, 'skeleton');
  };

  const handleAI = async () => {
    if (aiLoading) return;
    setAiLoading(true);
    try {
      const t = localStorage.getItem('access_token') || localStorage.getItem('token');
      const res = await fetch(`${API}/lyon/assist`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {}),
        },
        body: JSON.stringify({
          mode: 'rewrite',
          user_instruction: userText || verb.example_prompt,
          original_code: verb.skeleton,
          language: 'es',
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const generated = (data.suggested_code || data.reply || '').trim() || verb.skeleton;
      stashAndOpen(generated, 'ai');
    } catch (err) {
      console.error('[AVC] AI generation error:', err);
      toast.error('No pudimos generar con IA. Usá el skeleton como base.');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={() => !aiLoading && setOpen(false)}
      data-testid="avc-modal-overlay"
    >
      <div
        className="w-full max-w-lg bg-slate-900 border border-indigo-500/40 rounded-2xl shadow-2xl shadow-indigo-900/50 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="avc-modal"
      >
        <div className="flex items-start justify-between p-5 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-indigo-300 font-semibold">
                Auto-Visual Creation
              </div>
              <h2 className="text-lg font-bold text-white" data-testid="avc-modal-title">
                Detecté que querés crear un <span className="text-indigo-300">{verb.verb}</span>
              </h2>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            disabled={aiLoading}
            className="text-slate-400 hover:text-white p-1 rounded transition-colors disabled:opacity-40"
            data-testid="avc-close-btn"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-300">
            ¿Cómo querés empezar el <strong className="text-white">{verb.verb.toLowerCase()}</strong>?
          </p>
          {userText && (
            <div className="text-xs bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-400 italic" data-testid="avc-modal-user-text">
              &ldquo;{userText}&rdquo;
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Skeleton */}
            <button
              onClick={handleSkeleton}
              disabled={aiLoading}
              data-testid="avc-btn-skeleton"
              className="group text-left p-4 rounded-xl border border-emerald-500/40 bg-emerald-900/10 hover:bg-emerald-900/20 hover:border-emerald-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-2 mb-2">
                <FileCode className="w-5 h-5 text-emerald-300" />
                <span className="font-semibold text-white">Skeleton oficial</span>
              </div>
              <p className="text-xs text-slate-400">
                Empezá desde el preset estándar de DamyxIA. Instantáneo, sin IA.
              </p>
            </button>

            {/* AI */}
            <button
              onClick={handleAI}
              disabled={aiLoading}
              data-testid="avc-btn-ai"
              className="group text-left p-4 rounded-xl border border-indigo-500/40 bg-indigo-900/10 hover:bg-indigo-900/20 hover:border-indigo-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-2 mb-2">
                {aiLoading
                  ? <Loader2 className="w-5 h-5 text-indigo-300 animate-spin" />
                  : <Sparkles className="w-5 h-5 text-indigo-300" />}
                <span className="font-semibold text-white">
                  {aiLoading ? 'Generando…' : 'Generá con IA'}
                </span>
              </div>
              <p className="text-xs text-slate-400">
                Lyon adapta el skeleton a tu pedido y crea código listo para usar.
              </p>
            </button>
          </div>

          <div className="text-[11px] text-slate-500 pt-2 border-t border-slate-800">
            💡 Podés editar el código libremente en el Workspace una vez cargado.
          </div>
        </div>
      </div>
    </div>
  );
}
